import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { donationAPI } from '../lib/api';
import { Heart, Loader2, CheckCircle, ArrowRight, Gift, Globe, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function DomainDonationPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', domain_name: '', message: '', intended_recipient: 'any', registrar: '', estimated_value: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await donationAPI.submit(form);
      setSubmitted(true);
      toast.success('Donation request submitted!');
    } catch { toast.error('Failed to submit'); }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="px-6 md:px-12 max-w-7xl mx-auto py-20">
        <div className="max-w-lg mx-auto bg-[#0A0A0A] border border-white/5 rounded-sm p-12 text-center">
          <CheckCircle className="h-12 w-12 text-[#00FF94] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Thank You for Your Generosity!</h3>
          <p className="text-zinc-500">We'll process your domain donation and be in touch shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="donation-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Heart className="h-10 w-10 text-[#FF6B9D] mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t('services.donate')}</h1>
          <p className="text-zinc-500 mt-3 max-w-xl mx-auto">Give a domain a new life. Donate domains you no longer need and support the community.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {[
            { icon: Gift, title: 'Tax Benefits', desc: 'Domain donations may qualify as charitable contributions for tax purposes.' },
            { icon: Globe, title: 'Give Back', desc: 'Your unused domains could become the foundation of someone\'s next big project.' },
            { icon: Users, title: 'Community Support', desc: 'We redistribute donated domains to startups, non-profits, and educational projects.' },
          ].map((item, i) => (
            <div key={i} className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm">
              <item.icon className="h-8 w-8 text-[#00E5FF] mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2 font-['Outfit']">{item.title}</h3>
              <p className="text-sm text-zinc-500">{item.desc}</p>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 space-y-5" data-testid="donation-form">
          <h3 className="text-lg font-semibold text-white font-['Outfit']">Donate a Domain</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.name')}</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="donation-name" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.email')}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="donation-email" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Domain to Donate</Label>
            <Input value={form.domain_name} onChange={(e) => setForm(p => ({ ...p, domain_name: e.target.value }))} required
              className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" placeholder="yourdomain.com" data-testid="donation-domain" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Registrar</Label>
              <Input value={form.registrar} onChange={(e) => setForm(p => ({ ...p, registrar: e.target.value }))}
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" placeholder="e.g. GoDaddy, NameSilo" data-testid="donation-registrar" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Estimated Value ($)</Label>
              <Input type="number" value={form.estimated_value} onChange={(e) => setForm(p => ({ ...p, estimated_value: e.target.value }))}
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" placeholder="0" data-testid="donation-value" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Intended Recipient</Label>
              <select value={form.intended_recipient} onChange={(e) => setForm(p => ({ ...p, intended_recipient: e.target.value }))}
                className="mt-1.5 h-11 w-full bg-[#050505] border border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm px-3 text-sm" data-testid="donation-recipient">
                <option value="any">Anyone</option>
                <option value="nonprofit">Non-Profit</option>
                <option value="startup">Startup</option>
                <option value="education">Education</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Additional Notes</Label>
            <Textarea value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} rows={3}
              className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm resize-none" data-testid="donation-message" />
          </div>
          <Button type="submit" disabled={loading}
            className="h-11 px-8 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="donation-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Donate Domain <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>
        </form>
      </div>
    </div>
  );
}
